import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';

/**
 * Build a copy of the filter states with every filter reset to its empty
 * state. Category and search-query states pass through untouched, so clearing
 * never changes the category scope. Ranges reset to their full aggregated
 * bounds; list selections are unchecked and emptied.
 * @param filterStates The filter states to clear.
 * @returns The cleared filter states.
 */
export const getClearedFilterStates = (
    filterStates: FilterStates,
): FilterStates => ({
    ...filterStates,
    states: Object.fromEntries(
        Object.entries(filterStates.states).map(([key, value]) => {
            if (value) {
                if (isMultipleValuesFilterState(value)) {
                    return [
                        key,
                        {
                            ...value,
                            items: value.items.map((item) => ({
                                ...item,
                                checked: false,
                            })),
                            selectedItems: [],
                        },
                    ];
                }

                if (isRangeFilterState(value)) {
                    return [
                        key,
                        {
                            ...value,
                            value: { ...value.range },
                        },
                    ];
                }

                if (isSearchableListFilterState(value)) {
                    return [
                        key,
                        {
                            ...value,
                            items: value.items.map((item) => ({
                                ...item,
                                checked: false,
                            })),
                            selectedItems: [],
                        },
                    ];
                }

                if (isSingleValueFilterState(value)) {
                    return [
                        key,
                        {
                            ...value,
                            checked: false,
                        },
                    ];
                }

                if (isLocationFilterState(value)) {
                    return [
                        key,
                        {
                            ...value,
                            selectedLocation: null,
                        },
                    ];
                }
            }

            // Default case - return original value if type not recognized
            return [key, value];
        }),
    ) as FilterStates['states'],
    facet: filterStates.facet?.facetOptions
        ? {
              facetOptions: filterStates.facet.facetOptions.map(
                  (facetState) => ({
                      ...facetState,
                      state: {
                          ...facetState.state,
                          items: facetState.state.items.map((item) => ({
                              ...item,
                              checked: false,
                          })),
                          selectedItems: [],
                      },
                  }),
              ),
          }
        : undefined,
});
